import { trackContactForm } from "../lib/analytics";
import { Helmet } from "react-helmet-async";
import React, { useEffect, useState } from "react";
import { sendContact, getSiteSettings } from "../lib/db";
import { formatSupabaseError } from "../lib/supabase";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";

export default function Contatti() {
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    getSiteSettings()
      .then(setSettings)
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const f = new FormData(e.currentTarget);
    try {
      const payload = {
        name: f.get("name"),
        email: f.get("email"),
        subject: f.get("subject"),
        message: f.get("message"),
      };
      await sendContact(payload);
      await supabase.functions.invoke("contact-notify", { body: payload });
      trackContactForm();
      toast.success("Messaggio inviato!", { description: "Ti risponderemo al più presto." });
      e.target.reset();
    } catch (err) {
      console.error("ERRORE:", err);
      toast.error(err.message || "Errore sconosciuto");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contatti-page">
     <Helmet>
  <title>Contatti – ASD Rubiana Calcio</title>
  <meta name="description" content="Contatta l'ASD Rubiana Calcio. Indirizzo, telefono, email e modulo di contatto per informazioni su squadra, palestra e corsi in Val di Susa." />
  <meta property="og:title" content="Contatti – ASD Rubiana Calcio" />
  <meta property="og:description" content="Contatta l'ASD Rubiana Calcio per informazioni su squadra, palestra e corsi." />
  <meta property="og:url" content="https://rubianacalcio.it/contatti" />
  <meta property="og:type" content="website" />
  <meta property="og:image" content="https://rubianacalcio.it/logo512.png" />
</Helmet> 
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 border-b border-zinc-900">
        <div className="text-overline text-yellow-400 mb-4">// Contattaci</div>
        <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter uppercase leading-none">Parliamone</h1>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-5">
          <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-6">Trovaci a Rubiana</h2>
          {[
            { icon: MapPin, title: "Sede & impianti", body: settings.indirizzo || "Via dello Sport 12\n10040 Rubiana (TO)" },
            { icon: Phone, title: "Telefono", body: settings.telefono || "+39 011 123 4567" },
            { icon: Mail, title: "Email", body: settings.email || "info@rubianacalcio.it" },
            { icon: Clock, title: "Orari segreteria", body: settings.orari_palestra || "Lun-Ven: 17:00 – 20:00\nSab: 09:00 – 12:00" },
          ].map((c) => (
            <div key={c.title} className="flex gap-4 p-5 rounded-2xl bg-zinc-900 border border-zinc-800">
              <div className="h-10 w-10 shrink-0 rounded-lg bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                <c.icon className="h-4 w-4 text-yellow-400" />
              </div>
              <div>
                <div className="text-overline text-yellow-400">{c.title}</div>
                <div className="font-display font-medium mt-1 whitespace-pre-line text-zinc-200 text-sm">{c.body}</div>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="lg:col-span-7 rounded-3xl bg-zinc-900 border border-zinc-800 p-8 space-y-5" data-testid="contact-form">
          <h2 className="font-display font-bold text-2xl uppercase tracking-tight">Scrivici</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-zinc-300">Nome</Label>
              <Input id="name" name="name" required className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="contact-name" />
            </div>
            <div>
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input id="email" type="email" name="email" required className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="contact-email" />
            </div>
          </div>
          <div>
            <Label htmlFor="subject" className="text-zinc-300">Oggetto</Label>
            <Input id="subject" name="subject" required className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="contact-subject" />
          </div>
          <div>
            <Label htmlFor="message" className="text-zinc-300">Messaggio</Label>
            <Textarea id="message" name="message" rows={6} required className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="contact-message" />
          </div>
          <Button type="submit" disabled={submitting} className="bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-display font-bold rounded-full px-8 py-6" data-testid="contact-submit">
            {submitting ? "Invio…" : "Invia messaggio"}
          </Button>
        </form>
      </section>
    </div>
  );
}
